import React, { useRef, useState } from "react";
import {
  AlertDialog,
  AlertDialogBody,
  AlertDialogContent,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogOverlay,
  Box,
  Button,
  Flex,
  Icon,
  IconButton,
  Spinner,
  Text,
  Tooltip,
  VStack,
} from "@chakra-ui/react";
import { Header } from "build-ts/metadata";
import { ColumnType, MetadataTypeEnum } from "build-ts/enums";
import { MdBarChart, MdDelete, MdNumbers } from "react-icons/md";
import { FiCalendar, FiX } from "react-icons/fi";
import { PiTarget } from "react-icons/pi";
import { FaCheckCircle, FaChartPie } from "react-icons/fa";
import { findNodeColor, findNodeLabel } from "components/configuration/mkg/KnowledgeGraphNode";
import { useConfigurationContext } from "context/ConfigurationContext";
import EditMetricDefinitionContainer from "containers/configuration/EditMetricDefinitionContainer";
import EditBenchmarkComponent from "components/configuration/EditBenchmarkComponent";
import EditKGColumnComponent from "components/configuration/EditKGColumnComponent";
import DependenciesModal from "components/configuration/DependenciesModal";

const PANEL_W = 440;

function panelStyle(pos: { x: number; y: number }): React.CSSProperties {
  const screenW = window.innerWidth;
  const screenH = window.innerHeight;
  const maxH = Math.min(screenH * 0.74, 600);

  let left: number | undefined = pos.x + 18;
  let right: number | undefined;
  if (left + PANEL_W > screenW - 12) {
    left = undefined;
    right = screenW - pos.x + 18;
  }
  let top = pos.y - 40;
  if (top + maxH > screenH - 12) top = screenH - maxH - 12;
  if (top < 8) top = 8;

  return {
    position: "fixed",
    top,
    left,
    right,
    width: PANEL_W,
    maxHeight: maxH,
    zIndex: 1200,
    display: "flex",
    flexDirection: "column",
    backgroundColor: "white",
    borderRadius: 14,
    border: "1px solid #e2e8f0",
    boxShadow: "0 12px 40px rgba(0,0,0,0.14), 0 2px 8px rgba(0,0,0,0.08)",
    overflow: "hidden",
  };
}

function nodeTypeIcon(header: Header): React.ElementType {
  if (!header) return MdBarChart;
  if (header.type === MetadataTypeEnum.METRIC) return MdBarChart;
  if (header.type === MetadataTypeEnum.BENCHMARK) return PiTarget;
  if (header.type === MetadataTypeEnum.KG_COLUMN && header.column_type === ColumnType.ATTRIBUTE) return FaChartPie;
  if (header.type === MetadataTypeEnum.KG_COLUMN && header.column_type === ColumnType.MEASURE) return MdNumbers;
  if (header.type === MetadataTypeEnum.KG_COLUMN && header.column_type === ColumnType.TEMPORAL) return FiCalendar;
  return MdBarChart;
}

interface MKGNodePopupComponentProps {
  header: Header;
  clickPos: { x: number; y: number };
  onClose: () => void;
  handleDelete: () => Promise<void>;
  openDeleteBenchmark: boolean;
  closeDeleteBenchmark: () => void;
}

const MKGNodePopupComponent: React.FC<MKGNodePopupComponentProps> = ({
  header,
  clickPos,
  onClose,
  handleDelete,
  openDeleteBenchmark,
  closeDeleteBenchmark,
}) => {
  const { selectedMetric, selectedBenchmarkId, dependencies } = useConfigurationContext();

  const [triggerSubmit, setTriggerSubmit] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const cancelRef = useRef<HTMLButtonElement>(null);

  const nodeType = header.type ?? MetadataTypeEnum.METRIC;
  const color = findNodeColor(header);
  const typeLabel = findNodeLabel(header);
  const TypeIcon = nodeTypeIcon(header);

  return (
    <>
      <div style={panelStyle(clickPos)}>
        {/* ── Header strip ── */}
        <Flex
          align="center"
          gap={2}
          px={4}
          py={2.5}
          borderBottomWidth="1px"
          borderColor="gray.100"
          bg="gray.50"
          flexShrink={0}
        >
          <Flex
            w={6}
            h={6}
            borderRadius="full"
            bg={color}
            align="center"
            justify="center"
            flexShrink={0}
          >
            <Icon as={TypeIcon} color="white" boxSize={3.5} />
          </Flex>
          <VStack align="start" spacing={0} flex={1} minW={0}>
            <Text fontWeight="700" fontSize="sm" color="gray.900" noOfLines={1} mb={0} lineHeight="1.25">
              {header.name}
            </Text>
            <Text fontSize="xs" color="gray.500" mb={0} fontWeight="500">
              {typeLabel}
            </Text>
          </VStack>
          <Flex gap={1} flexShrink={0} align="center">
            <Button
              size="sm"
              colorScheme="green"
              leftIcon={<Icon as={FaCheckCircle} />}
              onClick={() => setTriggerSubmit(true)}
              fontWeight="600"
            >
              Save
            </Button>
            <Tooltip label="Delete" placement="top">
              <IconButton
                aria-label="Delete"
                icon={<Icon as={MdDelete} boxSize={4} />}
                size="sm"
                variant="ghost"
                colorScheme="red"
                onClick={() => setDeleteDialogOpen(true)}
              />
            </Tooltip>
            <Tooltip label="Close" placement="top">
              <IconButton
                aria-label="Close"
                icon={<Icon as={FiX} boxSize={4} />}
                size="sm"
                variant="ghost"
                colorScheme="gray"
                onClick={onClose}
              />
            </Tooltip>
          </Flex>
        </Flex>

        {/* ── Body ── */}
        <Box flex={1} overflowY="auto">
          {nodeType === MetadataTypeEnum.METRIC && (
            <EditMetricDefinitionContainer
              createMetricFlag={false}
              modelInfo={null}
              metricHeader={selectedMetric}
              onClose={onClose}
              triggerSubmit={triggerSubmit}
              setTriggerSubmit={setTriggerSubmit}
              compact
            />
          )}

          {nodeType === MetadataTypeEnum.BENCHMARK && (
            selectedBenchmarkId ? (
              <EditBenchmarkComponent
                benchmark={selectedBenchmarkId}
                onClose={onClose}
                triggerSubmit={triggerSubmit}
                setTriggerSubmit={setTriggerSubmit}
                compact
              />
            ) : (
              <Flex align="center" justify="center" py={10}>
                <Spinner size="md" color="purple.500" />
                <Text ml={3} fontSize="sm" color="gray.500" mb={0}>Loading…</Text>
              </Flex>
            )
          )}

          {nodeType === MetadataTypeEnum.KG_COLUMN && (
            <EditKGColumnComponent
              id={header.id}
              onClose={onClose}
              triggerSubmit={triggerSubmit}
              setTriggerSubmit={setTriggerSubmit}
              compact
            />
          )}
        </Box>
      </div>

      {/* ── Delete confirmation ── */}
      <AlertDialog
        isOpen={deleteDialogOpen}
        leastDestructiveRef={cancelRef}
        onClose={() => setDeleteDialogOpen(false)}
      >
        <AlertDialogOverlay style={{ backgroundColor: "rgba(0,0,0,0.15)" }}>
          <AlertDialogContent>
            <AlertDialogHeader fontSize="lg" fontWeight="bold">
              Delete {typeLabel}
            </AlertDialogHeader>
            <AlertDialogBody>
              Are you sure you want to delete <strong>{header.name}</strong>?
            </AlertDialogBody>
            <AlertDialogFooter>
              <Button ref={cancelRef} onClick={() => setDeleteDialogOpen(false)}>
                Cancel
              </Button>
              <Button colorScheme="red" onClick={handleDelete} ml={3}>
                Delete
              </Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>

      {/* ── Benchmark dependencies ── */}
      {dependencies.length > 0 && (
        <DependenciesModal
          isOpen={openDeleteBenchmark}
          onClose={closeDeleteBenchmark}
          dependencies={dependencies}
        />
      )}
    </>
  );
};

export default MKGNodePopupComponent;
